/* CPG fit flow (MOCKUP, 2026-10-02) — one shared fitment status for cart drawer, cart page,
 * product page and product cards. NEVER blocks Add to Cart or Checkout: it informs, and stamps
 * each cart line with the vehicle it was checked against + the result, so the order carries proof.
 *
 * Status per line (same words everywhere):
 *   fits        ✓ Fits your {vehicle}                       — our responsibility (fit guarantee)
 *   nofit       ✕ Does not fit your {vehicle}               — customer's choice to keep
 *   engine      ? Which engine is your {vehicle}?            — engine buttons
 *   unverified  Fitment not verified. Confirm by part number
 *   noveh       Vehicle not selected. Fit not checked
 *   universal   Universal fit, no vehicle needed
 *   spec        Sold by specification. Match your spec
 *
 * Data: the active garage vehicle (localStorage cpg:garage:vehicle via window.CPGFitment), the
 * product's custom.fitment_status (from /cart?view=cpgfit) and the existing read-only
 * /storefront/fitment/fit/bulk engine. No backend change.
 */
(function () {
  'use strict';
  if (window.CPGFitFlow) return;

  var BULK = 'https://carpartglobal-api-743237931312.us-central1.run.app/storefront/fitment/fit/bulk';

  function vehicle() { try { return window.CPGFitment && window.CPGFitment.getVehicle ? window.CPGFitment.getVehicle() : null; } catch (e) { return null; } }
  function vLabel(v) { return v ? [v.year, v.make, v.model, v.submodel, v.engine].filter(Boolean).join(' ') : ''; }
  function vShort(v) { return v ? [v.year, v.make, v.model].filter(Boolean).join(' ') : ''; }
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

  /** Engine-state map: API state → our status. */
  function fromApi(r) {
    if (!r || !r.state) return { status: 'unverified' };
    switch (r.state) {
      case 'FITS': case 'FITS_WITH_NOTES': return { status: 'fits', notes: r.notes || [] };
      case 'NO_FIT': return { status: 'nofit' };
      case 'NEEDS_QUALIFIER': return { status: 'engine', options: (r.options || []).map(function (o) { return o.value; }) }; // every engine the car came with — the shopper picks theirs, then sees the truth
      case 'UNIVERSAL': return { status: 'universal' };
      case 'BUILD_SPEC': return { status: 'spec' };
      default: return { status: 'unverified' };
    }
  }

  /** One fitment check for many products against one vehicle. */
  function bulk(v, pids) {
    if (!v || !pids.length) return Promise.resolve({});
    return fetch(BULK, { method: 'POST', headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ v: { year: v.year, make: v.make, model: v.model, submodel: v.submodel || null, engine: v.engine || null }, pids: pids }) })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (d) { return (d && d.results) || {}; })
      .catch(function () { return null; });
  }

  /** Status for a product with no vehicle context (cards / PDP before a vehicle is chosen). */
  function staticStatus(fs) {
    if (fs === 'universal') return 'universal';
    if (fs === 'build_spec') return 'spec';
    return 'noveh';
  }

  /** Customer-facing text for a status. */
  function text(st, v) {
    var name = vLabel(v);
    switch (st.status) {
      case 'fits': return '✓ Confirmed fit for your ' + vLabel(v); // matches the Fit Guarantee policy wording
      case 'nofit': return '✕ Does not fit your ' + name;
      case 'engine': return 'Which engine is your ' + name + '?';
      case 'universal': return 'Universal fit, no vehicle needed';
      case 'spec': return 'Sold by specification. Match your spec';
      case 'noveh': return 'Vehicle not selected. Fit not checked';
      case 'error': return 'Could not check fit right now';
      default: return 'Fitment not verified. Confirm by part number';
    }
  }
  /** What goes on the order (line property "Fitment"). */
  function proof(st) {
    switch (st.status) {
      case 'fits': return 'Confirmed fit';
      case 'nofit': return 'Does not fit selected vehicle (customer kept)';
      case 'engine': return 'Engine not confirmed';
      case 'universal': return 'Universal fit';
      case 'spec': return 'Sold by specification';
      case 'noveh': return 'No vehicle selected';
      case 'error': return 'Fit not checked (check failed)';
      default: return 'Not verified, customer confirms by part number';
    }
  }
  function confirmed(st) { return st.status === 'fits' || st.status === 'universal' || st.status === 'spec'; }

  // ONE vehicle for the whole cart (owner 2026-10-02; a per-line version was tried and reverted).
  // `_cpg_vehicle` is only referenced so the stamp removes it from lines that still carry it.
  var LV = '_cpg_vehicle';
  function cleanV(v) { return v && v.year && v.make && v.model ? { year: Number(v.year), make: String(v.make), model: String(v.model), submodel: v.submodel || null, engine: v.engine || null } : null; }
  function vKey(v) { v = cleanV(v); return v ? [v.year, v.make, v.model, v.submodel || '', v.engine || ''].join('|') : ''; }
  function baseKey(v) { v = cleanV(v); return v ? [v.year, v.make, v.model, v.submodel || ''].join('|') : ''; }
  function lineVehicle(it) { try { var s = it && it.properties && it.properties[LV]; return s ? cleanV(JSON.parse(s)) : null; } catch (e) { return null; } }
  function vehicleKeyed(status) { return status !== 'universal' && status !== 'spec'; }

  /** Read the cart + each product's fitment_status, then resolve every line against ITS vehicle. */
  function checkCart() {
    var garage = cleanV(vehicle());
    return Promise.all([
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
      fetch('/cart?view=cpgfit', { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (t) { try { return JSON.parse(t); } catch (e) { return { items: [] }; } }).catch(function () { return { items: [] }; })
    ]).then(function (res) {
      var cart = res[0], meta = {};
      (res[1].items || []).forEach(function (m) { meta[m.key] = m; });
      var lines = cart.items.filter(function (it) { return !(it.properties && it.properties._core_for); });
      var groups = {}; // vehicle key -> { v, pids }
      lines.forEach(function (it) {
        var fs = (meta[it.key] && meta[it.key].fs) || '';
        it.__fs = fs;
        it.__v = garage; // owner 2026-10-02: ONE vehicle for the whole cart (per-line reverted)
        if (fs === 'universal' || fs === 'build_spec' || !it.__v) return;
        var k = vKey(it.__v);
        (groups[k] = groups[k] || { v: it.__v, pids: [] }).pids.push(String(it.product_id));
      });
      var keys = Object.keys(groups);
      return Promise.all(keys.map(function (k) {
        var g = groups[k];
        return bulk(g.v, g.pids.filter(function (p, i, a) { return a.indexOf(p) === i; }));
      })).then(function (all) {
        var byVeh = {}; keys.forEach(function (k, i) { byVeh[k] = all[i]; });
        var out = {};
        lines.forEach(function (it) {
          var st, v = it.__v;
          if (it.__fs === 'universal' || it.__fs === 'build_spec') st = { status: staticStatus(it.__fs) };
          else if (!v) st = { status: 'noveh' };
          else if (byVeh[vKey(v)] === null) st = { status: 'error' };
          else st = fromApi((byVeh[vKey(v)] || {})[String(it.product_id)]);
          st.vehicle = vehicleKeyed(st.status) ? v : null;
          st.text = text(st, v);
          st.proof = proof(st);
          out[it.key] = st;
        });
        return { cart: cart, vehicle: garage, lines: out };
      });
    });
  }

  function wantedProps(it, st) {
    var props = {};
    Object.keys(it.properties || {}).forEach(function (k) { if (k !== 'Vehicle' && k !== 'Fitment' && k !== LV) props[k] = it.properties[k]; });
    if (st.vehicle) props.Vehicle = vLabel(st.vehicle);
    props.Fitment = st.proof;
    return props;
  }
  function sameProps(a, b) {
    var ka = Object.keys(a).filter(function (k) { return a[k] !== '' && a[k] != null; }).sort();
    var kb = Object.keys(b).filter(function (k) { return b[k] !== '' && b[k] != null; }).sort();
    return ka.join('\n') === kb.join('\n') && ka.every(function (k) { return String(a[k]) === String(b[k]); });
  }

  /** Write each line's vehicle + Vehicle + Fitment when they changed (proof on the order). Never
   *  loops: only lines whose stored values differ are touched, one request at a time. */
  var stamping = false;
  function stamp(result) {
    if (stamping) return Promise.resolve(false);
    var todo = result.cart.items.filter(function (it) {
      var st = result.lines[it.key]; if (!st) return false;
      return !sameProps(it.properties || {}, wantedProps(it, st));
    });
    if (!todo.length) return Promise.resolve(false);
    stamping = true;
    var chain = Promise.resolve();
    todo.forEach(function (it) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: wantedProps(it, result.lines[it.key]) }) }).catch(function () {});
      });
    });
    return chain.then(function () { stamping = false; return true; }, function () { stamping = false; return true; });
  }

  // ── shared look ──────────────────────────────────────────────────────────
  function css() {
    if (document.getElementById('cpg-fitflow-css')) return;
    var s = document.createElement('style'); s.id = 'cpg-fitflow-css';
    s.textContent =
      '.cpg-ff-badge{display:inline-flex;align-items:center;gap:6px;margin-top:6px;padding:5px 9px;border-radius:7px;font-size:12.5px;font-weight:600;line-height:1.3;max-width:100%}' +
      '.cpg-ff-badge--fits{background:#e8f6ee;color:#11733f}.cpg-ff-badge--nofit{background:#fdecec;color:#b42318}' +
      '.cpg-ff-badge--engine{background:#fff6e0;color:#8a5a00}.cpg-ff-badge--universal,.cpg-ff-badge--spec{background:#e8f1fd;color:#1d4ed8}' +
      '.cpg-ff-badge--noveh,.cpg-ff-badge--unverified,.cpg-ff-badge--error{background:#f1f2f4;color:#4b5563}' +
      '.cpg-ff-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}' +
      '.cpg-ff-btn{border:1px solid #d0d5dd;background:#fff;color:#111;border-radius:7px;padding:5px 10px;font-size:12px;font-weight:600;line-height:1.2;cursor:pointer;font-family:inherit}' +
      '.cpg-ff-btn--primary{background:#e3141b;border-color:#e3141b;color:#fff}' +
      '.cpg-ff-link{background:none;border:0;padding:4px 0;color:#374151;font-size:12px;font-weight:600;text-decoration:underline;cursor:pointer;font-family:inherit}' +
      '.cpg-ff-veh{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 16px;border-bottom:1px solid #eee;font-size:13px;line-height:1.35}' +
      '.cpg-ff-veh--none{background:#fff6e0;color:#5c3d00}.cpg-ff-veh--set{background:#e8f6ee;color:#11733f}' +
      '.cpg-ff-veh strong{font-weight:700}.cpg-ff-veh .cpg-ff-btn{white-space:nowrap}' +
      '.cpg-ff-note{margin:0 0 10px;padding:7px 10px;border-radius:8px;background:#fff6e0;color:#5c3d00;font-size:12px;line-height:1.35}' +
      '.cpg-ff-note--ok{background:#e8f6ee;color:#11733f}';
    document.head.appendChild(s);
  }

  function badgeHtml(st, opts) {
    opts = opts || {};
    var key = opts.key ? esc(opts.key) : '';
    var h = '<div class="cpg-ff-badge cpg-ff-badge--' + st.status + '">' + esc(st.text) + '</div>';
    var a = [];
    if (st.status === 'engine' && st.options && st.options.length) {
      st.options.slice(0, 6).forEach(function (o) { a.push('<button type="button" class="cpg-ff-btn" data-ff-engine="' + esc(o) + '">' + esc(o) + '</button>'); });
    }
    if (st.status === 'nofit' && key) {
      a.push('<button type="button" class="cpg-ff-btn" data-ff-remove="' + key + '">Remove</button>');
      a.push('<button type="button" class="cpg-ff-btn" data-ff-pick>Use a different vehicle</button>');
    }
    if (st.status === 'noveh' && opts.showPick) a.push('<button type="button" class="cpg-ff-btn cpg-ff-btn--primary" data-ff-pick>Select vehicle</button>');
    return h + (a.length ? '<div class="cpg-ff-actions">' + a.join('') + '</div>' : '');
  }

  /** Vehicle bar at the top of the cart drawer / cart page: the ONE vehicle every part is checked for. */
  function vehicleBarHtml(v, anyNeedsVehicle) {
    if (v) return '<div class="cpg-ff-veh cpg-ff-veh--set"><span>Checking your parts for <strong>' + esc(vLabel(v)) + '</strong></span><button type="button" class="cpg-ff-btn" data-ff-pick>Change</button></div>';
    if (!anyNeedsVehicle) return '';
    return '<div class="cpg-ff-veh cpg-ff-veh--none"><span><strong>Which vehicle are these parts for?</strong><br>Select it to check every part in your cart.</span><button type="button" class="cpg-ff-btn cpg-ff-btn--primary" data-ff-pick>Select vehicle</button></div>';
  }

  /** Note above the checkout button. Never blocks. */
  function noteHtml(result) {
    var keys = Object.keys(result.lines), n = 0, bad = 0, fit = 0;
    keys.forEach(function (k) { var s = result.lines[k]; if (!confirmed(s)) n++; if (s.status === 'nofit') bad++; if (s.status === 'fits') fit++; });
    if (!keys.length) return '';
    // The Fit Guarantee covers only ✓ Confirmed fit parts; Universal and Sold by specification parts are
    // excluded by the refund policy, so the note never promises the guarantee for them.
    if (!n && fit === keys.length) return '<div class="cpg-ff-note cpg-ff-note--ok">✓ Every part in your cart is confirmed. Fit guarantee applies.</div>';
    if (!n && fit) return '<div class="cpg-ff-note cpg-ff-note--ok">✓ Every part in your cart is checked. Fit guarantee covers the ✓ Confirmed fit items.</div>';
    if (!n) return '<div class="cpg-ff-note cpg-ff-note--ok">These parts need no vehicle check.</div>';
    // Each item is counted once: a "does not fit" item is not also counted as "not confirmed".
    var other = n - bad;
    return '<div class="cpg-ff-note">' + (bad ? bad + ' item' + (bad > 1 ? 's do' : ' does') + ' not fit your selected vehicle. ' : '') +
      (other ? other + ' item' + (other > 1 ? 's are' : ' is') + ' not confirmed for your vehicle. ' : '') +
      'Checkout is open; fit guarantee covers ✓ items.</div>';
  }

  // Vehicle stepper, same look as the Shop by Vehicle stepper: Year > Make > Model > Trim > Engine.
  var API = 'https://carpartglobal-api-743237931312.us-central1.run.app/storefront/fitment';
  function jget(u) { return fetch(API + u, { headers: { accept: 'application/json' } }).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); }); }
  function qs(o) { return Object.keys(o).filter(function (k) { return o[k]; }).map(function (k) { return k + '=' + encodeURIComponent(o[k]); }).join('&'); }
  function stepCss() {
    if (document.getElementById('cpg-ffstep-css')) return;
    var st = document.createElement('style'); st.id = 'cpg-ffstep-css';
    st.textContent =
      '.cpg-ffs{position:fixed;inset:0;z-index:2147483600;display:flex;align-items:center;justify-content:center;padding:16px}' +
      '.cpg-ffs[hidden]{display:none}.cpg-ffs__bg{position:absolute;inset:0;background:rgba(16,17,20,.6)}' +
      '.cpg-ffs__panel{position:relative;width:100%;max-width:580px;background:#fff;border-radius:16px;box-shadow:0 24px 60px -18px rgba(16,17,20,.55);padding:24px;max-height:88vh;overflow:auto;font-family:inherit;color:#0f1419}' +
      '.cpg-ffs__x{position:absolute;top:12px;right:12px;width:36px;height:36px;border:0;background:#f1f2f4;border-radius:9px;font-size:22px;line-height:1;cursor:pointer;color:#4b5563}' +
      '.cpg-ffs__kick{display:block;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#e3141b}' +
      '.cpg-ffs__veh{font-size:21px;font-weight:800;text-transform:uppercase;margin:6px 40px 0 0;min-height:26px}' +
      '.cpg-ffs__prog{display:flex;flex-wrap:wrap;gap:12px;margin:14px 0 18px;font-size:12.5px;color:#5a6470}' +
      '.cpg-ffs__prog span{display:flex;align-items:center;gap:6px}.cpg-ffs__prog b{width:22px;height:22px;border-radius:50%;background:#e8eaed;color:#5a6470;display:inline-flex;align-items:center;justify-content:center;font-size:11px}' +
      '.cpg-ffs__prog .on{color:#0f1419;font-weight:700}.cpg-ffs__prog .on b{background:#0f1419;color:#fff}.cpg-ffs__prog .done b{background:#1a8754;color:#fff}' +
      '.cpg-ffs__h{font-size:19px;font-weight:800;text-transform:uppercase;margin:0 0 12px}' +
      '.cpg-ffs__grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}.cpg-ffs__grid--wide{grid-template-columns:repeat(2,1fr)}' +
      '.cpg-ffs__opt{border:1px solid #d6d9de;background:#fff;border-radius:9px;padding:13px 8px;font-size:16px;font-weight:700;color:#0f1419;cursor:pointer;text-align:center;font-family:inherit;line-height:1.25}' +
      '.cpg-ffs__opt:hover,.cpg-ffs__opt:focus-visible{border-color:#e3141b;background:#e3141b;color:#fff}' +
      '.cpg-ffs__find{box-sizing:border-box;width:100%;margin:0 0 10px;padding:12px;border:1px solid #d6d9de;border-radius:9px;font-size:16px;font-family:inherit}' +
      '.cpg-ffs__row{display:flex;justify-content:space-between;align-items:center;margin-top:16px;gap:10px}' +
      '.cpg-ffs__link{background:none;border:0;color:#e3141b;font-size:15px;font-weight:700;cursor:pointer;text-decoration:underline;padding:6px 0;font-family:inherit}' +
      '.cpg-ffs__load{padding:30px 0;text-align:center;color:#5a6470;font-size:15px}' +
      '@media(max-width:480px){.cpg-ffs__grid{grid-template-columns:repeat(3,1fr)}.cpg-ffs__grid--wide{grid-template-columns:1fr}.cpg-ffs__panel{padding:20px 16px}.cpg-ffs__veh{font-size:18px}}';
    document.head.appendChild(st);
  }
  /** opts.onPick(vehicle): use the picked vehicle instead of saving it as the garage vehicle.
   *  opts.title: heading above the stepper. */
  function openStepper(opts) {
    opts = opts && typeof opts === 'object' && !opts.target ? opts : {};
    stepCss();
    var root = document.getElementById('cpgFfStepper');
    if (!root) {
      root = document.createElement('div'); root.id = 'cpgFfStepper'; root.className = 'cpg-ffs'; root.hidden = true;
      root.innerHTML = '<div class="cpg-ffs__bg" data-ffs-close></div><div class="cpg-ffs__panel" role="dialog" aria-modal="true" aria-label="Select your vehicle">' +
        '<button type="button" class="cpg-ffs__x" data-ffs-close aria-label="Close">&times;</button>' +
        '<span class="cpg-ffs__kick">Select your vehicle</span><div class="cpg-ffs__veh" data-ffs-veh></div>' +
        '<div class="cpg-ffs__prog" data-ffs-prog></div><div data-ffs-stage></div></div>';
      document.body.appendChild(root);
      root.addEventListener('click', function (e) { if (e.target.closest('[data-ffs-close]')) closeStepper(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) closeStepper(); });
    }
    var kick = root.querySelector('.cpg-ffs__kick'); if (kick) kick.textContent = opts.title || 'Select your vehicle';
    var stage = root.querySelector('[data-ffs-stage]'), vehEl = root.querySelector('[data-ffs-veh]'), prog = root.querySelector('[data-ffs-prog]');
    var V = { year: '', make: '', model: '', submodel: '', engine: '' }, STEPS = ['Year', 'Make', 'Model', 'Trim', 'Engine'], token = 0;
    function closeStepper() { root.hidden = true; document.documentElement.style.overflow = ''; }
    function head(i) {
      vehEl.textContent = [V.year, V.make, V.model, V.submodel].filter(Boolean).join(' ');
      prog.innerHTML = STEPS.map(function (n, k) { return '<span class="' + (k < i ? 'done' : k === i ? 'on' : '') + '"><b>' + (k < i ? '&#10003;' : (k + 1)) + '</b>' + n + '</span>'; }).join('');
    }
    function loading(t) { stage.innerHTML = '<div class="cpg-ffs__load">' + esc(t) + '</div>'; return ++token; }
    function failed() { stage.innerHTML = '<div class="cpg-ffs__load">Could not load the list. Please try again.</div><div class="cpg-ffs__row"><button type="button" class="cpg-ffs__link" data-ffs-back>Back</button><span></span></div>'; var bk = stage.querySelector('[data-ffs-back]'); if (bk) bk.addEventListener('click', back); }
    function grid(title, items, wide, onPick, skipLabel) {
      var find = items.length > 24 ? '<input class="cpg-ffs__find" type="search" placeholder="Type to filter" aria-label="Filter" data-ffs-find>' : '';
      stage.innerHTML = '<h3 class="cpg-ffs__h">' + esc(title) + '</h3>' + find +
        '<div class="cpg-ffs__grid' + (wide ? ' cpg-ffs__grid--wide' : '') + '">' + items.map(function (x) { return '<button type="button" class="cpg-ffs__opt" data-ffs-val="' + esc(x) + '">' + esc(x) + '</button>'; }).join('') + '</div>' +
        '<div class="cpg-ffs__row">' + (V.year ? '<button type="button" class="cpg-ffs__link" data-ffs-back>Back</button>' : '<span></span>') +
        (skipLabel ? '<button type="button" class="cpg-ffs__link" data-ffs-skip>' + esc(skipLabel) + '</button>' : '') + '</div>';
      stage.querySelectorAll('[data-ffs-val]').forEach(function (b) { b.addEventListener('click', function () { onPick(b.getAttribute('data-ffs-val')); }); });
      var f = stage.querySelector('[data-ffs-find]');
      if (f) f.addEventListener('input', function () { var q = f.value.toLowerCase(); stage.querySelectorAll('[data-ffs-val]').forEach(function (b) { b.style.display = b.textContent.toLowerCase().indexOf(q) > -1 ? '' : 'none'; }); });
      var bk = stage.querySelector('[data-ffs-back]'); if (bk) bk.addEventListener('click', back);
      var sk = stage.querySelector('[data-ffs-skip]'); if (sk) sk.addEventListener('click', function () { onPick(''); });
    }
    function back() {
      if (V.submodel) { V.submodel = ''; return stepTrim(); }
      if (V.model) { V.model = ''; return stepModel(); }
      if (V.make) { V.make = ''; return stepMake(); }
      V.year = ''; return stepYear();
    }
    function stepYear() {
      head(0); var t = loading('Loading years');
      jget('/years').then(function (d) { if (t !== token) return; grid('What year is your vehicle?', (d.years || []).map(String).sort(function (a, b) { return b - a; }), false, function (y) { V.year = y; stepMake(); }); }).catch(failed);
    }
    function stepMake() {
      head(1); var t = loading('Loading makes');
      jget('/makes?' + qs({ year: V.year })).then(function (d) { if (t !== token) return; grid('Which make?', d.makes || [], true, function (m) { V.make = m; stepModel(); }); }).catch(failed);
    }
    function stepModel() {
      head(2); var t = loading('Loading models');
      jget('/models?' + qs({ year: V.year, make: V.make })).then(function (d) { if (t !== token) return; grid('Which model?', d.models || [], true, function (m) { V.model = m; stepTrim(); }); }).catch(failed);
    }
    function stepTrim() {
      head(3); var t = loading('Checking trims');
      jget('/submodels?' + qs({ year: V.year, make: V.make, model: V.model })).then(function (d) {
        if (t !== token) return;
        var subs = d.submodels || [];
        if (!subs.length) return stepEngine();
        grid('Which trim?', subs, true, function (s) { V.submodel = s; stepEngine(); }, 'Not sure, skip');
      }).catch(function () { if (t === token) stepEngine(); });
    }
    function stepEngine() {
      head(4); var t = loading('Checking engines');
      jget('/engines?' + qs({ year: V.year, make: V.make, model: V.model })).then(function (d) {
        if (t !== token) return;
        var e = d.engines || [];
        if (!e.length) return save('');
        if (e.length === 1) return save(e[0]);
        grid('Which engine?', e, true, function (x) { save(x); }, 'Not sure, skip');
      }).catch(function () { if (t === token) save(''); });
    }
    function save(engine) {
      V.engine = engine || '';
      var picked = { year: V.year, make: V.make, model: V.model, submodel: V.submodel || null, engine: V.engine || null };
      closeStepper();
      if (typeof opts.onPick === 'function') { opts.onPick(picked); return; }
      if (window.CPGFitment && window.CPGFitment.saveVehicle) window.CPGFitment.saveVehicle(picked);
    }
    root.hidden = false; document.documentElement.style.overflow = 'hidden';
    stepYear();
  }

  // Shared click handling (drawer + cart page + PDP use the same data attributes).
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-ff-pick],[data-ff-engine],[data-ff-remove]') : null;
    if (!t) return;
    e.preventDefault();
    // Garage vehicle (product page, cards): just the stepper.
    if (t.hasAttribute('data-ff-pick')) { openStepper(); return; }
    if (t.hasAttribute('data-ff-engine')) { if (window.CPGFitment && window.CPGFitment.setEngine) window.CPGFitment.setEngine(t.getAttribute('data-ff-engine')); return; }
    if (t.hasAttribute('data-ff-remove')) {
      fetch('/cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: t.getAttribute('data-ff-remove'), quantity: 0 }) })
        .then(function () { document.dispatchEvent(new CustomEvent('cpg:fitflow-cart-changed')); });
    }
  });

  window.CPGFitFlow = {
    vehicle: vehicle, vLabel: vLabel, vShort: vShort, bulk: bulk, fromApi: fromApi, staticStatus: staticStatus,
    text: text, proof: proof, confirmed: confirmed, checkCart: checkCart, stamp: stamp,
    css: css, openStepper: openStepper, badgeHtml: badgeHtml, vehicleBarHtml: vehicleBarHtml, noteHtml: noteHtml, esc: esc
  };
  css();
})();
